import { IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

export default function Input(props: any) {
  const {
    disabled,
    label,
    name,
    value,
    onChange,
    onBlur,
    onClick,
    InputProps,
    type = "text",
    placeholder,
    error,
    helperText,
    fullWidth = true,
    height,
    width,
    backgroundColor,
    children,
    sx = {},
    ...rest
  } = props;

  const isNativePickerType = ["date", "time", "datetime-local", "month", "week"].includes(type);

  const handleFieldClick = (event: any) => {
    onClick?.(event);
    if (event.defaultPrevented || disabled || !isNativePickerType) return;

    const input = event.currentTarget.querySelector("input") as HTMLInputElement | null;
    if (!input || input.disabled || input.readOnly) return;
    // The compact date input is for typing; the remaining field and icon open the picker.
    if (type === "date" &&
        (event.target === input || !event.target.closest(".MuiInputBase-root"))) return;

    try {
      input.focus();
      input.showPicker?.();
    } catch {
      input.focus();
    }
  };

  return (
    <div style={{ width: fullWidth ? "100%" : width }}>
      {label && (
        <Typography
          variant="body1"
          fontWeight={700}
          fontSize={14}
          color="#1F2937"
          sx={{ display: "block", textAlign: "left", mb: 0.65, lineHeight: 1.35 }}
        >
          {label}
        </Typography>
      )}
      <TextField
        disabled={disabled}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onClick={handleFieldClick}
        type={type}
        placeholder={placeholder}
        fullWidth={fullWidth}
        error={error}
        helperText={helperText}
        InputLabel={{ shrink: false }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "var(--case-field-radius, 8px)",
            height: height ?? "var(--case-field-height, 60px)",
            width: width,
            backgroundColor: backgroundColor,
            fontSize: 14,
            transition: "border-color 160ms ease, box-shadow 160ms ease",
            cursor: isNativePickerType && !disabled ? "pointer" : undefined,
          },
          "& .MuiOutlinedInput-root.Mui-disabled": {
            backgroundColor: backgroundColor || "#FAFAFA",
          },
          "& .MuiOutlinedInput-input.Mui-disabled": {
            WebkitTextFillColor: "#5F6676",
          },
          "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button":
          {
            WebkitAppearance: "none",
            margin: 0,
          },
          "& input[type=number]": {
            MozAppearance: "textfield",
          },
          "& input[type=date], & input[type=time], & input[type=datetime-local], & input[type=month], & input[type=week]": {
            cursor: disabled ? "default" : type === "date" ? "text" : "pointer",
          },
          "& input[type=date]": {
            flex: "0 1 12ch",
            width: "12ch",
            minWidth: 0,
          },
          "& input[type=date]::-webkit-calendar-picker-indicator": {
            display: "none",
          },
          "& .MuiSelect-select": {
            textAlign: "left",
          },
          ...sx,
        }}
        select={props.select}
        SelectProps={{
          MenuProps: {
            PaperProps: {
              sx: {
                backgroundColor: "#ffffff",
                borderRadius: "12px",
                maxHeight: "300px",
              },
            },
          },
        }}
        {...rest}
        InputProps={type === "date" ? {
          ...InputProps,
          endAdornment: (
            <InputAdornment position="end" sx={{ ml: "auto" }}>
              {InputProps?.endAdornment}
              <IconButton
                aria-label={`Open ${typeof label === "string" ? label : "date"} calendar`}
                size="small"
                disabled={disabled || InputProps?.readOnly || props.inputProps?.readOnly}
              >
                <CalendarMonthIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        } : InputProps}
      >
        {children}
      </TextField>
    </div>
  );
}
